<template>
  <div class="component order" ref="container" v-bind:style="containerStyle">
    <vue-style type="text/css" id="vuetify-theme">
      {{ vuetifyThemeCss }}
    </vue-style>
    <vue-style>
      {{ customStyle }}
    </vue-style>
    <vue-style>
        :host {
            --primaryColor: {{primaryColor}};
            --backgroundColor: {{backgroundColor}};
            --titleColor: {{titleColor}};
            --textColor: {{textColor}};
            --primary-color: var(--primaryColor);
        }

        .order .theme--light.v-sheet {
        background-color: {{backgroundColor}};
        }

        .order body,
        .order h1,
        .order h2,
        .order h3,
        .order h4,
        .order h5,
        .order h6,
        .order span,
        .order p {
        color: {{textColor}} !important;
        }

        .theme--light.v-stepper--vertical .v-stepper__content:not(:last-child) {
        border-left-color: {{textColor}} !important;
        }

        .order .find-order-id .v-text-field__details {
        display: none;
        }

        .order label {
        color: {{titleColor}} !important;
        }

        .order .primary {
        background-color: {{primaryColor}} !important;
        }

        .order .secondary {

        }

        .order .theme--light.v-counter,
        .order .theme--light.v-icon,
        .order .theme--light.v-input:not(.v-input--is-disabled) input,
        .order .theme--light.v-input:not(.v-input--is-disabled) textarea {
        color: {{textColor}};
        }

        .order .theme--light.v-label,
        .order .theme--light.v-sheet {
        color: {{titleColor}};
        }
    </vue-style>
    <template v-if="isLoading">
      <div class="spinner-container">
        <spinner></spinner>
      </div>
    </template>
    <template v-if="couldNotFindOrder">
      <div class="find-order-id">
        <v-card>
          <v-card-text>
            <h4>
              Order ID <strong>{{ initialOrderId }}</strong> is invalid.
              <template v-if="showInputOnOrderNotFound"
                ><br />Please correct it and retry.</template
              >
            </h4>
            <template v-if="showInputOnOrderNotFound">
              <div class="text-right">
                <v-text-field
                  outlined
                  type="text"
                  v-model="newOrderId"
                  placeholder="Order ID"
                >
                  <template slot="append-outer">
                    <v-btn color="primary" v-on:click.prevent="searchOrderId"
                      >Track</v-btn
                    >
                  </template>
                </v-text-field>
              </div>
            </template>
            <template v-else>
              <v-btn color="primary" v-on:click.prevent="goToOrderCreation"
                >Close</v-btn
              >
            </template>
          </v-card-text>
        </v-card>
      </div>
    </template>
    <template v-else-if="errorMessage">
      <v-card>
        <v-card-text>
          <h1>Oops!</h1>

          <h3>Our support is notified about this incident!</h3>
          <h4>
            Please retry or contact support at
            <strong>support@runbuggy.com</strong> if the problem persists.
          </h4>
          <br /><strong>888-87-BUGGY (888-872-8449)</strong>
          <p>
            <strong>Error Code:</strong> ERROR<br />
            <strong>Error Description:</strong>&nbsp;<span
              v-html="errorMessage"
            ></span>
          </p>
          <br />
          <div class="text-right">
            <v-btn color="primary" v-on:click="goToOrderCreation">Retry</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </template>
    <template v-else-if="formJson">
      <json-page
        v-bind:json-string="formJson"
        v-bind:debug="debug"
        v-bind:task-id="taskID"
        v-bind:variables="variables"
        v-bind:key="jsonPageKey"
        v-on:exception="onJsonPageException"
        v-on:redirect="onRedirect"
        v-on:submitted="onSubmitted"
      >
      </json-page>
    </template>
    <template v-else-if="isJsonForms">
      <v-app>
        <camunda-resolved-json-forms
          :url="camundaUrl"
          :taskId="taskID"
          locale="en"
          @load-request="camundaOnLoadRequest"
          @load-response="camundaOnLoadResponse"
          @load-error="camundaOnLoadError"
          @submit-request="camundaOnSubmitRequest"
          @submit-response="camundaOnSubmitResponse"
          @submit-error="camundaOnSubmitError"
        >
        </camunda-resolved-json-forms>
      </v-app>
    </template>
    <template v-if="debug">
      <hr />
      <div class="container">
        <div class="row">
          <div class="col-4">
            <strong>processInstance</strong>
          </div>
          <div class="col-8">
            {{ processInstance }}
          </div>
        </div>

        <div class="row">
          <div class="col-4">
            <strong>errorMessage</strong>
          </div>
          <div class="col-8">
            {{ errorMessage }}
          </div>
        </div>

        <div class="row">
          <div class="col-4">
            <strong>instanceVariables</strong>
          </div>
          <div class="col-8">
            {{ instanceVariables }}
          </div>
        </div>

        <div class="row">
          <div class="col-4">
            <strong>formJson</strong>
          </div>
          <div class="col-8">
            {{ formJson }}
          </div>
        </div>

        <div class="row">
          <div class="col-4">
            <strong>vin</strong>
          </div>
          <div class="col-8">
            {{ vin }}
          </div>
        </div>

        <div class="row">
          <div class="col-4">
            <strong>userEmail</strong>
          </div>
          <div class="col-8">
            {{ userEmail }}
          </div>
        </div>

        <div class="row">
          <div class="col-4">
            <strong>from</strong>
          </div>
          <div class="col-8">
            {{ from }}
          </div>
        </div>

        <div class="row">
          <div class="col-4">
            <strong>to</strong>
          </div>
          <div class="col-8">
            {{ to }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script type="text/babel">
import Vue from "vue";
import {
  VContainer,
  VFlex,
  VCard,
  VCardText,
  VLayout,
  VBtn,
  VTextField,
  VApp,
} from "vuetify/lib";
import store from "@/store";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import _each from "lodash/each";
import _cloneDeep from "lodash/cloneDeep";
import UrlMixin from "@/mixins/url";
import CamundaApiService from "@/services/camundaApi";

import { CamundaResolvedJsonForms } from "@runbuggy/camunda-jsonforms";
import config from "@/config";

import JsonPage from "@/components/json-page";
import VueStyle from "@/components/style";
import Spinner from "@/components/spinner";
import vuetify from "../plugins/vuetify";
import Notifications from "vue-notification";
import InternationalizationPlugin from "@/plugins/i18n";
import * as shadyCss from 'shady-css-parser';
import LoadScript from 'vue-plugin-load-script';

// This is needed because of the json-ref resolution when the schema url is not specified or it is relative
import * as process from "process";
window.process = process;

Vue.use(Notifications);
Vue.use(InternationalizationPlugin);
Vue.use(LoadScript);
Vue.config.productionTip = false;

const EXCEPTION_NO_TASKS = "EXCEPTION_NO_TASKS";
const EXCEPTION_NO_USER_EMAIL = "EXCEPTION_NO_USER_EMAIL";
const EXCEPTION_NO_VIN = "EXCEPTION_NO_VIN";
const EXCEPTION_COMPLETED = "EXCEPTION_COMPLETED";
const EXCEPTION_NO_PROCESS_ID = "EXCEPTION_NO_PROCESS_ID";
const EXCEPTION_UNABLE_TO_FIND_EXISTING_INSTANCE =
  "EXCEPTION_UNABLE_TO_FIND_EXISTING_INSTANCE";
const EXCEPTION_UNABLE_CREATE_NEW_INSTANCE =
  "EXCEPTION_UNABLE_CREATE_NEW_INSTANCE";

const EVENT_TASK_LOADED = "taskLoaded";
const EVENT_INSTANCE_COMPLETED = "instanceCompleted";

/*
    Order Widget
    This widget integrates with workflows such as order placement and registration

    If an Order ID prop is passed it will try to load an existing order directly, if not it will show order input

     */
export default {
  vuetify,
  store,
  name: "views-order-index",
  watch: {},
  props: {
    processInstanceIdQueryParam: undefined,
    // The background color
    backgroundColor: {
      default() {
        return "#ffffff";
      },
    },
    // The title color
    titleColor: {
      default() {
        return "#0e8dc4";
      },
    },
    // The text color
    textColor: {
      default() {
        return "#06225a";
      },
    },
    // The primary color
    primaryColor: {
      default() {
        return "#0e8dc4";
      },
    },
    processDefinitionKey: {
      required: true,
    },

    /**
     * Continue the instance if a combo is found between VIN and Email
     */
    continueInstanceFromVinAndEmail: {
      default() {
        return false;
      },
    },

    // JSON string of Variables that are set on instance creation
    initVariablesJson: undefined,

    // Show input on order not found
    showInputOnOrderNotFound: undefined,
    // The order ID we're trying to search for
    orderId: undefined,
    // The user's email address (used for account creation / mapping)
    userEmail: undefined,
    // The VIN number for the transported vehicle
    vin: undefined,
    // A valid address that represents the pick-up point for the vehicle
    from: undefined,
    // A valid address that represents the drop-off point for the vehicle
    to: undefined,
    // The user's first name (used during registration)
    firstName: undefined,
    // The user's last name (used during registration)
    lastName: undefined,
    // The user's phone number (used during registration)
    phoneNumber: undefined,
    // The user's company name (used during registration)
    companyName: undefined,
    // The pre-calculated fare amount
    fare: undefined,
    // The API token used to make requests
    token: {
      required: false,
    },
    customStyle: {
      required: false,
      type: String,
    },
  },
  data() {
    return {
      initialOrderId: this.orderId,
      jsonPageKey: undefined,
      newOrderId: undefined,
      errorMessage: undefined,
      processInstance: undefined,
      isLoading: false,
      loadStepRetryCount: 0,
      loadStepRetryCountMax: 5,
      loadStepErrorMessage: undefined,
      loadStepErrorType: undefined,
      task: undefined,
      formJson: undefined,
      instanceVariables: undefined,
      couldNotFindOrder: false,
      lastHeight: undefined,
      vuetifyTheme: vuetify.framework.theme,
    };
  },
  methods: {
    clearProcessInstanceIdToHistory() {
      if (!this.processInstanceIdQueryParam) {
        return;
      }

      let currentSearchParams = new URLSearchParams(window.location.search);
      currentSearchParams.delete(this.processInstanceIdQueryParam);
      const newPath =
        window.location.pathname + "?" + currentSearchParams.toString();
      window.history.replaceState(null, "", newPath);
    },
    appendProcessInstanceIdToHistory() {
      let currentSearchParams = new URLSearchParams(window.location.search);

      if (
        this.processInstanceIdQueryParam &&
        currentSearchParams.get(this.processInstanceIdQueryParam) !==
          this.processInstanceID
      ) {
        currentSearchParams.set(
          this.processInstanceIdQueryParam,
          this.processInstanceID
        );
        const newPath =
          window.location.pathname + "?" + currentSearchParams.toString();
        window.history.replaceState(null, "", newPath);
      }
    },
    onJsonPageException(err) {
      this.handleException(err);
    },
    camundaOnLoadRequest(requestInfo, requestInit) {
      let token = window.RUNBUGGY_TOKEN;

      requestInit.headers = requestInit.headers || {};
      if (token) {
        requestInit.headers["Authorization"] = `Bearer ${token}`;
      } else {
        requestInit.headers["Authorization"] = `Basic YW5vbnltb3VzOg==`;
      }
    },
    camundaOnLoadResponse(response) {},
    camundaOnLoadError(error) {
      if (
        error.name === "AppException" &&
        (error.code === "RETRIEVE_TASK_DEPLOYED_FORM" ||
          error.code === "INVALID_TASK_DEPLOYED_FORM_RESPONSE" ||
          error.code === "RETRIEVE_PROCESS_DEFINITION_DEPLOYED_START_FORM" ||
          error.code ===
            "INVALID_PROCESS_DEFINITION_DEPLOYED_START_FORM_RESPONSE")
      ) {
        // ignore loading from deployed forms - most likely JsonFormsFormServicePlugin was not installed - just log the error in the console
        return;
      }
      if (
        error.name === "ResponseException" &&
        (error.response.request.url.endsWith("/deployed-start-form") ||
          error.response.request.url.endsWith("/deployed-form"))
      ) {
        // ignore loading from deployed forms - most likely JsonFormsFormServicePlugin was not installed - just log the error in the console
        return;
      }
      this.handleException(error);
    },
    camundaOnSubmitRequest(requestInfo, requestInit) {
      if (config.camunda.enableAuth) {
        let token = window.RUNBUGGY_TOKEN;

        requestInit.headers = requestInit.headers || {};
        if (token) {
          requestInit.headers["Authorization"] = `Bearer ${token}`;
        } else {
          requestInit.headers["Authorization"] = `Basic YW5vbnltb3VzOg==`;
        }
      }
    },
    camundaOnSubmitResponse(response) {
      if (response.status >= 200 && response.status < 300) {
        this.onSubmitted();
      }
    },
    camundaOnSubmitError(error) {
      if (error.name === "AppException" && error.response) {
        let response = error.response;
        if (response.status == 401) {
          this.handleException(new Error(`You are not authenticated`));
          return;
        } else if (response.status == 403) {
          this.handleException(new Error(`You are not authorized`));
          return;
        }
      }
      this.handleException(error);
    },
    goToOrderCreation() {
      this.newOrderId = undefined;
      this.initialOrderId = undefined;
      this.couldNotFindOrder = false;
      this.clearProcessInstanceIdToHistory();
      this.init(true);
    },
    saveHeight() {
      this.lastHeight = _get(this.$refs, "container.offsetHeight");
    },
    clearHeight() {
      this.lastHeight = undefined;
    },
    searchOrderId() {
      this.init();
    },
    init(startFresh) {
      this.errorMessage = undefined;
      let promise = new Promise((resolve) => {
        resolve();
      });

      if (!startFresh && this.initialOrderId) {
        this.couldNotFindOrder = false;
        return (promise = this.findExistingInstance()
          .then((processInstance) => {
            this.processInstance = processInstance;
          })
          .then(() => {
            return this.loadStep();
          })
          .catch(() => {
            this.couldNotFindOrder = true;
          }));
      }

      let initialProcessInstanceId =
        this.initialProcessInstanceId || this.processInstanceIdFromQueryParam;
      if (!startFresh && initialProcessInstanceId) {
        this.couldNotFindOrder = false;
        return (promise = this.findExistingProcessInstanceById(
          initialProcessInstanceId
        )
          .then((processInstance) => {
            this.processInstance = processInstance;
          })
          .then(() => {
            return this.loadStep();
          })
          .catch(() => {
            this.couldNotFindOrder = true;
            this.goToOrderCreation();
          }));
      }

      if (
        !startFresh &&
        this.continueInstanceFromVinAndEmail &&
        this.userEmail &&
        this.vin
      ) {
        promise = promise
          .then(() => {
            return this.findExistingInstance();
          })
          .then((existingInstance) => {
            if (existingInstance) {
              this.processInstance = existingInstance;
            } else {
              return this.createNewInstance().then((processInstance) => {
                this.processInstance = processInstance;
              });
            }
          });
      } else {
        promise = promise
          .then(() => {
            return this.createNewInstance();
          })
          .then((processInstance) => {
            this.processInstance = processInstance;
          });
      }

      promise = promise.catch((err) => {
        this.handleException(err);
      });

      return promise
        .then(() => {
          return this.loadStep();
        })
        .catch((err) => {
          this.handleException(err);
        });
    },
    handleException(err) {
      console.log(err);
      let message = err.message;

      switch (message) {
        case EXCEPTION_NO_USER_EMAIL:
          this.errorMessage = "No user-email value provided";
          break;
        case EXCEPTION_NO_VIN:
          this.errorMessage = "No VIN value provided";
          break;
        case EXCEPTION_COMPLETED:
          this.goToOrderCreation();
          break;
        case EXCEPTION_NO_TASKS:
          this.errorMessage = `No tasks available`;
          break;
        case EXCEPTION_UNABLE_TO_FIND_EXISTING_INSTANCE:
          this.errorMessage = `Unable to find existing instance`;
          break;
        case EXCEPTION_UNABLE_CREATE_NEW_INSTANCE:
          this.errorMessage = `Unable to create a new instance`;
          break;
        case EXCEPTION_NO_PROCESS_ID:
          this.errorMessage = `No process id`;
          break;
        default:
          this.errorMessage = message;
          break;
      }
    },
    onRedirect(url) {
      if (url === window.location.href) {
        window.location.reload();
      } else {
        window.location = url;
      }
    },
    onSubmitted() {
      this.saveHeight();
      this.$nextTick(() => {
        this.loadStep().catch((err) => {
          this.handleException(err);
        });
      });
    },
    loadStep() {
      if (!this.processInstanceID) {
        throw new Error(EXCEPTION_NO_PROCESS_ID);
      }

      this.isLoading = true;
      this.loadStepRetryCount++;
      this.loadStepErrorMessage = undefined;
      this.loadStepErrorType = undefined;

      return CamundaApiService.getProcessInstanceHistory(this.processInstanceID)
        .then((res) => {
          let state = _get(res, "state");

          switch (state) {
            case "COMPLETED":
              this.isLoading = false;

              this.$el.dispatchEvent(
                new CustomEvent(EVENT_INSTANCE_COMPLETED, {
                  bubbles: true,
                  composed: true,
                })
              );

              throw new Error(EXCEPTION_COMPLETED);
            default:
              break;
          }
        })
        .then(() => {
          return CamundaApiService.getProcessInstanceTasks(
            this.processInstanceID
          );
        })
        .then((task) => {
          if (task.length === 0) {
            throw new Error(EXCEPTION_NO_TASKS);
          } else {
            this.task = task[0];
          }
        })
        .then(() => {
          if (!this.isJsonForms) {
            return CamundaApiService.getTaskForm(this.taskID);
          }
        })
        .then((data) => {
          if (!this.isJsonForms) {
            this.formJson = JSON.stringify(data);
          } else {
            this.formJson = null;
          }
        })
        .then(() => {
          if (!this.isJsonForms) {
            return CamundaApiService.getProcessInstanceVariables(
              this.processInstanceID
            );
          }
        })
        .then((instanceVariables) => {
          if (!this.isJsonForms) {
            this.instanceVariables =
              CamundaApiService.deserializeInstanceVariables(instanceVariables);
          } else {
            this.instanceVariables = null;
          }
        })
        .then(() => {
          this.loadStepRetryCount = 0;
          this.isLoading = false;
        })
        .catch((err) => {
          this.handleException(err);
          this.isLoading = false;
        })
        .finally(() => {
          this.jsonPageKey = new Date().getTime();
          this.clearHeight();

          this.$nextTick(() => {
            this.appendProcessInstanceIdToHistory();
            let eventDetail = _cloneDeep(this.task);
            this.$set(eventDetail, "instanceVariables", this.mappedInstanceVariables);
            const event = new CustomEvent(EVENT_TASK_LOADED, {
              bubbles: true,
              detail: eventDetail,
              composed: true,
            });
            this.$el.dispatchEvent(event);
          });
        });
    },
    createNewInstance() {
      let variables = {
        vin: this.vin,
        fromAddress: this.from,
        toAddress: this.to,
        email: this.userEmail,
        firstName: this.firstName,
        lastName: this.lastName,
        phoneNumber: this.phoneNumber,
        companyName: this.companyName,
        fare: this.fare,
      };

      variables = {
        ...variables,
        ...this.initializationVariables,
      };

      Object.keys(variables).forEach((key) => {
        let value = variables[key];
        if (value !== null) {
          variables[key] = CamundaApiService.encodeVariable(value);
        }
      });

      return CamundaApiService.startProcessInstanceKey(
        this.processDefinitionKey,
        {
          variables: variables,
        }
      )
        .then((res) => {
          return res;
        })
        .catch((err) => {
          console.log(err);
          throw new Error(EXCEPTION_UNABLE_CREATE_NEW_INSTANCE);
        });
    },
    findExistingProcessInstanceById(processInstanceId) {
      return CamundaApiService.getProcessInstanceTasks(processInstanceId)
        .then((res) => {
          return res.shift();
        })
        .catch((err) => {
          console.log(err);
          throw new Error(EXCEPTION_UNABLE_TO_FIND_EXISTING_INSTANCE);
        });
    },
    findExistingInstance() {
      let queryVariables;

      if (this.newOrderId || this.initialOrderId) {
        queryVariables = [
          {
            name: "orderId",
            operator: "eq",
            value: this.newOrderId || this.initialOrderId,
          },
        ];
      } else {
        queryVariables = [
          {
            name: "vin",
            operator: "eq",
            value: this.vin,
          },
          {
            name: "email",
            operator: "eq",
            value: this.userEmail,
          },
        ];
      }

      return CamundaApiService.findProcessInstanceFromHistory({
        processDefinitionKey: this.processDefinitionKey,
        variables: queryVariables,
      })
        .then((res) => {
          return res.shift();
        })
        .catch((err) => {
          console.log(err);
          throw new Error(EXCEPTION_UNABLE_TO_FIND_EXISTING_INSTANCE);
        });
    },
    setFontStyle() {
      let linkNode = document.createElement("link");
      linkNode.type = "text/css";
      linkNode.rel = "stylesheet";
      linkNode.href =
        "//fonts.googleapis.com/css?family=Roboto:100,300,400,500,700,900|Material+Icons";
      document.head.appendChild(linkNode);

      linkNode = document.createElement("link");
      linkNode.type = "text/css";
      linkNode.rel = "stylesheet";
      linkNode.href =
        "//fonts.googleapis.com/css?family=Roboto:100,300,400,500,700,900|Material+Icons";
      this.$refs["container"].appendChild(linkNode);
    },
    // include the fonts outside the webcomponent for now - https://github.com/google/material-design-icons/issues/1165
    injectShadowFontsInDocument() {
      const root = this.$el.getRootNode();
      if (root instanceof ShadowRoot && root.hasChildNodes()) {
        const parser = new shadyCss.Parser();
        class FaceFontStringifier extends shadyCss.Stringifier {
          insideFontFace = false;
          visit(node) {
            if (node.type === shadyCss.nodeType.stylesheet) {
              return super.visit(node);
            }
            if (
              node.type === shadyCss.nodeType.atRule &&
              node.name === 'font-face'
            ) {
              try {
                this.insideFontFace = true;
                return super.visit(node);
              } finally {
                this.insideFontFace = false;
              }
            }
            return this.insideFontFace ? super.visit(node) : '';
          }
        }
        const faceFontStringifier = new FaceFontStringifier();

        // we have css with scope so we should have unique id
        const rootTemplate = this.$refs['container'];
        const dataAttrNames = rootTemplate
          .getAttributeNames()
          .filter((an) => an.startsWith('data-v-'));
        const uniqueId =
          dataAttrNames && dataAttrNames.length > 0 ? dataAttrNames[0] : '';

        let children = root.childNodes;
        for (let i = 0; i < children.length; i++) {
          const node = children[i];
          if (node.nodeName.toLowerCase() === 'style' && node.textContent) {
            try {
              const id = `runbuggy-order-${uniqueId}-ff-${i}`;
              let el = document.querySelector(`style[id="${id}"]`);
              if (!el) {
                el = document.createElement('style');
                el.id = id;
                const ast = parser.parse(node.textContent);
                el.textContent = faceFontStringifier.stringify(ast);
                document.head.appendChild(el);
              }
            } catch (e) {
              console.log(e);
            }
          }
        }
      }
    },    
  },
  computed: {
    mappedInstanceVariables() {
      let mapped = {};
      _each(this.instanceVariables, (variable, key) => {
          mapped[key] = variable.value;
      });
      return mapped;
    },
    processInstanceIdFromQueryParam() {
      let currentSearchParams = new URLSearchParams(window.location.search);
      return currentSearchParams.get(this.processInstanceIdQueryParam);
    },
    initializationVariables() {
      let variables = {};

      let initVarsJson = this.initVariablesJson;
      if (initVarsJson) {
        try {
          variables = JSON.parse(initVarsJson);
        } catch (e) {
          console.error("Could not parse init-variables-json string", e);
        }
      }

      return variables;
    },
    containerStyle() {
      let style = {};

      if (this.lastHeight) {
        style.minHeight = `${this.lastHeight}px`;
      }

      return style;
    },
    variables() {
      return {
        instanceVariables: this.instanceVariables,
      };
    },
    debug() {
      return this.getQueryParameter("runbuggyDebug");
    },
    processInstanceID() {
      return _get(
        this.processInstance,
        "processInstanceId",
        _get(this.processInstance, "id")
      );
    },
    taskID() {
      return _get(this.task, "id");
    },
    taskFormKey() {
      return _get(this.task, "formKey");
    },
    isJsonForms() {
      return this.taskFormKey
        ? this.taskFormKey.startsWith(
            "embedded:app:webjars/forms/jsonforms.html"
          )
        : false;
    },
    camundaUrl() {
      return `${config.camunda.api.path}`;
    },
    vuetifyThemeCss() {
      return this.vuetifyTheme ? this.vuetifyTheme.generatedStyles : undefined;
    },
  },
  created() {},
  mounted() {
    if (this.token) {
      window.RUNBUGGY_TOKEN = this.token;
    }

    this.setFontStyle();
    this.init();
    this.injectShadowFontsInDocument();
  },
  beforeDestroy() {},
  mixins: [UrlMixin],
  components: {
    VLayout,
    VContainer,
    VFlex,
    VBtn,
    VCard,
    VCardText,
    VTextField,
    JsonPage,
    CamundaResolvedJsonForms,
    VueStyle,
    Spinner,
    VApp,
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
@import "~bootstrap/scss/bootstrap";
@import '~@fontsource/roboto/index.css';
@import '~@mdi/font/css/materialdesignicons.min.css';
@import '~vuetify/dist/vuetify.min.css';
@import '~@jsonforms/vue2-vuetify/lib/jsonforms-vue2-vuetify.esm.css';
@import '~@runbuggy/common-jsonforms/lib/common-jsonforms.esm.css';

.order {
  box-sizing: border-box;
  -ms-overflow-style: scrollbar;
  position: relative;
  min-height: 50px;

  .spinner-container {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    padding-top: 25%;
    background: rgba(0, 0, 0, 0.01);
    backdrop-filter: blur(2px);
  }

  *,
  *::before,
  *::after {
    box-sizing: inherit;
  }

  h5 {
    font-size: 18px;
    line-height: 24px;
    font-weight: 400;
  }

  p {
    font-size: 12px;
    line-height: 24px;
    font-weight: 500;
  }
}
</style>
